<template>
  <section
    class="assembly-node-list-panel"
    data-onboarding="branch-panel"
    :class="{ 'assembly-node-list-panel--busy': actionBusy }"
    :style="themeColorVars"
  >
    <header class="assembly-node-list-panel__header">
      <div class="assembly-node-list-panel__heading">
        <h2 class="assembly-node-list-panel__title">节点列表</h2>
        <p class="assembly-node-list-panel__subtitle">{{ assemblyNodeName }}</p>
      </div>
      <div class="assembly-node-list-panel__summary">
        <n-tooltip
          trigger="hover"
          placement="top"
          :show-arrow="false"
          :theme-overrides="tooltipThemeOverrides"
          :content-style="tooltipContentStyle"
        >
          <template #trigger>
            <n-button
              class="assembly-node-list-panel__refresh-button"
              size="tiny"
              circle
              quaternary
              aria-label="刷新节点列表"
              :aria-busy="loading"
              :loading="loading"
              :disabled="refreshButtonDisabled"
              @click="handleRefreshClick"
            >
              <template #icon>
                <n-icon>
                  <Renew />
                </n-icon>
              </template>
            </n-button>
          </template>
          <span>刷新节点列表</span>
        </n-tooltip>
        <n-tag
          class="assembly-node-list-panel__count"
          size="small"
          round
          :bordered="false"
        >
          {{ nodeCountText }}
        </n-tag>
      </div>
    </header>

    <n-spin
      class="assembly-node-list-panel__spin"
      :show="loading"
      size="small"
    >
      <n-empty
        v-if="nodes.length === 0"
        class="assembly-node-list-panel__empty"
        description="暂无子节点"
        size="small"
      />
      <div
        v-else
        class="assembly-node-list-panel__body"
      >
        <AssemblyBranchNodeCard
          v-for="node in nodes"
          :key="node.nodeId"
          :node="node"
          :readonly-preview="readonlyPreview"
          @switch-version="emit('switchVersion', node)"
          @edit-responsible-branch="emit('editResponsibleBranch', node, $event)"
          @hover-change="handleNodeHoverChange(node, $event)"
        />
      </div>
    </n-spin>
  </section>
</template>

<script setup lang="ts">
/**
 * 装配体分支子节点列表面板。
 *
 * 面板只负责渲染列表、空态和入口动作，所有业务副作用都通过事件交由 Editor/controller 处理。
 */
import { computed } from 'vue';
import type { CSSProperties } from 'vue';
import { NButton, NEmpty, NIcon, NSpin, NTag, NTooltip } from 'naive-ui';
import { Renew } from '@vicons/carbon';
import AssemblyBranchNodeCard from '@/components/editor/AssemblyBranchNodeCard.vue';
import { useEditorTooltipTheme } from '@/hooks/useEditorTooltipTheme';
import type {
  AssemblyChildNodeBranchSummary,
  AssemblyChildNodeSummary,
} from '@/services/assembly-branch-node-list';
import { useGlobalConfigStore } from '@/stores';

const props = defineProps<{
  /** 当前装配体子节点名称，用于提示列表上下文。 */
  assemblyNodeName: string;
  /** 当前装配体分支下的直接子节点列表。 */
  nodes: readonly AssemblyChildNodeSummary[];
  /** 是否正在加载节点列表。 */
  loading: boolean;
  /** 是否正在切换采纳版本。 */
  switchingVersion: boolean;
  /** 只读预览态不允许创建或编辑。 */
  readonlyPreview: boolean;
}>();

const emit = defineEmits<{
  /** 打开切换采纳版本弹窗。 */
  switchVersion: [node: AssemblyChildNodeSummary];
  /** 进入负责分支工作区，由 Editor 统一生成新标签页路由。 */
  editResponsibleBranch: [node: AssemblyChildNodeSummary, branch: AssemblyChildNodeBranchSummary];
  /** 同步节点动态高亮状态。 */
  hoverNode: [node: AssemblyChildNodeSummary, hovered: boolean];
  /** 请求刷新当前装配体分支下的子节点列表。 */
  refresh: [];
}>();

const globalConfigStore = useGlobalConfigStore();
const { tooltipThemeOverrides, tooltipContentStyle } = useEditorTooltipTheme({
  maxWidth: '180px',
});

type AssemblyNodeListThemeVars = CSSProperties & Record<string, string>;

const isDarkTheme = computed(() => Boolean(globalConfigStore.getProviderTheme()));

/**
 * 为节点列表提供主题化的卡片色阶。
 *
 * 这里把色彩变量集中放在面板层，让卡片只消费变量，避免暗黑/亮色模式下每张卡片各自推导导致层级不一致。
 */
const themeColorVars = computed<AssemblyNodeListThemeVars>(() => {
  if (!isDarkTheme.value) {
    return {
      '--assembly-node-list-card-bg': '#f8fafc',
      '--assembly-node-list-card-hover-bg': '#f3f7fb',
      '--assembly-node-list-card-border': '#d6dee8',
      '--assembly-node-list-card-hover-border': '#b8cadf',
      '--assembly-node-list-card-shadow': '0 1px 2px rgb(15 23 42 / 7%)',
      '--assembly-node-list-version-bg': '#eef4fb',
      '--assembly-node-list-version-border': '#c7d8ea',
      '--assembly-node-list-part-version-bg': '#eef8f3',
      '--assembly-node-list-part-version-border': '#bfdccf',
      '--assembly-node-list-assembly-version-bg': '#eef4fb',
      '--assembly-node-list-assembly-version-border': '#c7d8ea',
    };
  }

  return {
    '--assembly-node-list-card-bg': '#202832',
    '--assembly-node-list-card-hover-bg': '#263241',
    '--assembly-node-list-card-border': '#354253',
    '--assembly-node-list-card-hover-border': '#49627d',
    '--assembly-node-list-card-shadow': 'inset 0 1px 0 rgb(255 255 255 / 4%), 0 1px 2px rgb(0 0 0 / 24%)',
    '--assembly-node-list-version-bg': '#18304a',
    '--assembly-node-list-version-border': '#386486',
    '--assembly-node-list-part-version-bg': '#17382a',
    '--assembly-node-list-part-version-border': '#3f7658',
    '--assembly-node-list-assembly-version-bg': '#18304a',
    '--assembly-node-list-assembly-version-border': '#386486',
  };
});

/**
 * 生成节点数量文案。
 *
 * @returns 当前列表中的子节点数量说明。
 */
const nodeCountText = computed(() => `${props.nodes.length} 个子节点`);

/**
 * 聚合动作 loading 状态。
 *
 * @returns 版本切换进行中时返回 true，用于根节点附加状态类。
 */
const actionBusy = computed(() => props.switchingVersion);

/**
 * 判断刷新按钮是否应临时禁用。
 *
 * 设计说明：列表刷新和采纳版本切换都会改写同一个节点列表 Store。按钮在这些过程里禁用，可以避免用户
 * 重复触发相同上下文的加载，也避免切换版本的局部同步被手动刷新打断。
 *
 * @returns 当前不适合触发手动刷新时返回 true。
 */
const refreshButtonDisabled = computed(() => props.loading || props.switchingVersion);

/**
 * 触发节点列表刷新事件。
 */
function handleRefreshClick(): void {
  if (refreshButtonDisabled.value) {
    return;
  }

  emit('refresh');
}

/**
 * 转发子节点卡片的动态高亮状态。
 *
 * 说明：面板仍然只负责列表层事件汇聚，保持模型定位和 Viewer 调用都收口在 Editor。
 *
 * @param node 当前发生 hover 状态变化的子节点。
 * @param hovered 是否处于鼠标移入状态。
 */
function handleNodeHoverChange(node: AssemblyChildNodeSummary, hovered: boolean): void {
  emit('hoverNode', node, hovered);
}
</script>

<style scoped>
.assembly-node-list-panel {
  /* frontend-design：紧凑 CAD 侧栏仅使用 Naive UI 主题变量和派生色，保证亮色/暗色主题可读。 */
  --assembly-node-list-bg: var(--editor-side-panel-drawer-bg, var(--n-card-color, var(--n-color)));
  --assembly-node-list-text: var(--n-text-color);
  --assembly-node-list-text-2: color-mix(in srgb, var(--assembly-node-list-text) 70%, var(--assembly-node-list-bg) 30%);
  --assembly-node-list-text-3: color-mix(in srgb, var(--assembly-node-list-text) 54%, var(--assembly-node-list-bg) 46%);
  --assembly-node-list-muted-text: var(--assembly-node-list-text-3);
  --assembly-node-list-divider: var(
    --editor-side-panel-divider,
    color-mix(in srgb, var(--assembly-node-list-text) 14%, transparent)
  );
  --assembly-node-list-hover: color-mix(in srgb, var(--n-primary-color) 7%, transparent);
  --assembly-node-list-card-bg: var(--assembly-node-list-bg);
  --assembly-node-list-card-hover-bg: var(--assembly-node-list-card-bg);
  --assembly-node-list-branch-hover-bg: transparent;
  --assembly-node-list-card-border: color-mix(in srgb, var(--assembly-node-list-text) 14%, transparent);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: var(--assembly-node-list-bg);
  color: var(--assembly-node-list-text);
}

.assembly-node-list-panel--busy {
  cursor: progress;
}

.assembly-node-list-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--editor-side-panel-header-gap, 10px);
  padding: var(--editor-side-panel-header-padding, 14px 14px 12px);
  border-bottom: 1px solid var(--assembly-node-list-divider);
  background: var(--assembly-node-list-bg);
}

.assembly-node-list-panel__heading {
  min-width: 0;
  padding-top: 1px;
}

.assembly-node-list-panel__title {
  margin: 0;
  color: var(--assembly-node-list-text);
  font-size: var(--editor-side-panel-title-size, 15px);
  font-weight: var(--editor-side-panel-title-weight, 700);
  line-height: var(--editor-side-panel-title-line-height, 1.35);
}

.assembly-node-list-panel__subtitle {
  min-width: 0;
  margin: 3px 0 0;
  overflow: hidden;
  color: var(--assembly-node-list-muted-text);
  font-size: 12px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assembly-node-list-panel__summary {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
}

.assembly-node-list-panel__count {
  background: color-mix(in srgb, var(--n-text-color) 8%, transparent);
  color: var(--n-text-color-2);
  font-weight: 600;
}

.assembly-node-list-panel__refresh-button {
  color: var(--assembly-node-list-text-2);
}

.assembly-node-list-panel__refresh-button:hover,
.assembly-node-list-panel__refresh-button:focus-visible {
  color: var(--n-primary-color);
}

.assembly-node-list-panel__spin {
  flex: 1;
  min-height: 0;
}

.assembly-node-list-panel__spin :deep(.n-spin-content) {
  height: 100%;
  min-height: 0;
}

.assembly-node-list-panel__body {
  box-sizing: border-box;
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding: 12px 14px 16px;
}

.assembly-node-list-panel__empty {
  padding: 48px 0;
}
</style>
